import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

let agentInstance: BrowserAgent | null = null;

// Returns false for errors that didn't originate from this app's own bundle.
// This React app is injected as a single <script> tag onto a Shopify storefront
// page, so New Relic's window-level error hooks also see the store theme's JS,
// other installed apps, and tracking pixels. Each deploy is served from a
// dedicated, environment-specific base URL (see VITE_BASE_URL), so a stack
// frame referencing that URL is a reliable signal the error came from us.
const isOwnBundleError = (error: Error | string): boolean => {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  // Local dev only ever serves this app, so there's nothing else to filter out.
  if (!baseUrl || !baseUrl.startsWith('http')) return true;

  const stack = typeof error === 'string' ? error : error.stack || error.message || '';
  return stack.includes(baseUrl);
};

// New Relic Browser Agent Configuration - Error Tracking Only
export const initNewRelic = () => {
  // Only initialize if we have the required environment variables
  const accountId = import.meta.env.VITE_NEWRELIC_ACCOUNT_ID;
  const trustKey = import.meta.env.VITE_NEWRELIC_TRUST_KEY;
  const agentId = import.meta.env.VITE_NEWRELIC_AGENT_ID;
  const licenseKey = import.meta.env.VITE_NEWRELIC_LICENSE_KEY;
  const applicationId = import.meta.env.VITE_NEWRELIC_APPLICATION_ID;

  // Check if New Relic is configured
  if (!accountId || !trustKey || !agentId || !licenseKey || !applicationId) {
    console.warn('New Relic is not configured. Skipping initialization.');
    return null;
  }

  try {
    // Initialize the Browser Agent with minimal configuration for error tracking only
    const options = {
      init: {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.nr-data.net'] },
        custom_attributes: {
          domain: window.location.hostname,
          environment: import.meta.env.VITE_ENV
        }
      },
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey: licenseKey,
        applicationID: applicationId,
        sa: 1,
        source_map_enabled: true
      },
      loader_config: {
        accountID: accountId,
        trustKey: trustKey,
        agentID: agentId,
        licenseKey: licenseKey,
        applicationID: applicationId
      }
    };

    agentInstance = new BrowserAgent(options);
    agentInstance.setCustomAttribute('environment', import.meta.env.VITE_ENV);

    // Drop errors that didn't come from this app's own bundle (see
    // isOwnBundleError above) so Shopify theme/app noise never reaches New
    // Relic in the first place.
    agentInstance.setErrorHandler((error) => !isOwnBundleError(error));

    console.log('New Relic Browser Agent initialized successfully (Error Tracking Only)');
    return agentInstance;
  } catch (error) {
    console.error('Failed to initialize New Relic:', error);
    return null;
  }
};

// Export a function to manually report errors with custom context
export const reportError = (error: Error, customAttributes?: Record<string, string | number | boolean>) => {
  // Try window.newrelic first (set by the agent)
  if (window.newrelic) {
    window.newrelic.noticeError(error, customAttributes);
  } else if (agentInstance) {
    // Fallback to agent instance
    agentInstance.noticeError(error, customAttributes);
  } else {
    // If New Relic is not initialized, log to console
    console.error('New Relic not initialized. Error:', error, customAttributes);
  }
};

// Test function to verify New Relic is working
export const testNewRelic = () => {
  const test_var = {new: 1};
  test_var.foo.bar;
};
